import { defaultsDeep } from 'lodash'
import { fetchJson } from '../utils/fetch'
import { firebase } from '../utils/firebase'
import { Offer } from '../types/offers'

type FirebaseDynamicLinkRequestBody = {
  dynamicLinkInfo?: {
    domainUriPrefix?: string
    link?: string
    androidInfo?: {
      androidPackageName?: string
      androidFallbackLink?: string
      androidMinPackageVersionCode?: string
    }
    iosInfo?: {
      iosBundleId?: string
      iosFallbackLink?: string
      iosCustomScheme?: string
      iosIpadFallbackLink?: string
      iosIpadBundleId?: string
      iosAppStoreId?: string
    }
    navigationInfo?: {
      enableForcedRedirect?: boolean
    }
    analyticsInfo?: {
      googlePlayAnalytics?: {
        utmSource?: string
        utmMedium?: string
        utmCampaign?: string
        utmTerm?: string
        utmContent?: string
      }
      itunesConnectAnalytics?: {
        at?: string
        ct?: string
        mt?: string
        pt?: string
      }
    }
    socialMetaTagInfo?: {
      socialTitle?: string
      socialDescription?: string
      socialImageLink?: string
    }
  }
  suffix?: {
    option?: 'SHORT' | 'UNGUESSABLE'
  }
}

type FirebaseDynamicLinkResponseBody = {
  shortLink: string
  previewLink: string
}

const API_URL: string = 'https://firebasedynamiclinks.googleapis.com/v1/shortLinks'

const DEFAULT_DYNAMIC_LINK_CONFIG: Partial<FirebaseDynamicLinkRequestBody> = Object.freeze({
  dynamicLinkInfo: {
    domainUriPrefix: process.env.REACT_APP_FIREBASE_DYNAMIC_LINK_DOMAIN,
    iosInfo: {
      iosBundleId: 'com.thebuzzjar.buzzjar',
      iosAppStoreId: '1632614007',
    },
    androidInfo: {
      androidPackageName: 'app.buzzl',
    },
  },
})

export type SocialMetadata = {
  title?: string
  description?: string
  imageLink?: string
}

async function createDeepLink(link: string, socialMetadata?: SocialMetadata): Promise<string> {
  const { apiKey } = firebase.options
  if (!apiKey) {
    throw new Error('No Firebase API key found')
  }
  const query = { key: apiKey }

  const requestBody: FirebaseDynamicLinkRequestBody = {
    dynamicLinkInfo: {
      link: link,
      socialMetaTagInfo: {
        socialTitle: socialMetadata?.title,
        socialDescription: socialMetadata?.description,
        socialImageLink: socialMetadata?.imageLink,
      },
    },
  }
  const body = defaultsDeep(requestBody, DEFAULT_DYNAMIC_LINK_CONFIG)
  const { shortLink } = await fetchJson<FirebaseDynamicLinkResponseBody>('POST', API_URL, {
    body,
    query,
  })

  return shortLink
}

const DEEP_LINK_BASE = `https://buzzl-web.netlify.app`

class DeepLinksService {
  createToOffer = async (offer: Pick<Offer, 'id' | 'title' | 'description' | 'image'>): Promise<string> => {
    const offerLink = `${DEEP_LINK_BASE}/offer/${offer.id}`
    const socialMetadata: SocialMetadata = {
      title: offer.title,
      description: offer.description,
      imageLink: offer.image?.url,
    }
    const deepLink = await createDeepLink(offerLink, socialMetadata)

    return deepLink
  }
}

const deepLinksService = new DeepLinksService()

export default deepLinksService
